import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import axios from 'axios';
import { toast } from 'react-toastify';
import Invoices from './Invoices';
import authService from './api-authorization/AuthorizeService';

interface Invoice {
    loading: boolean;
    gridLoading: boolean;
    invoiceDetails: InvoiceDetails[];
    invoiceTotalFormatted: string;
    canEdit: boolean;
    canEditHirePeriod: boolean;
    canView: boolean;
    canSend: boolean;
    canUpdatePaid: boolean;
    page: number;
    totalRecords: number;
    pageSize: number;
    customerId: number;
    siteAddress: string;
    jobId: string;
    invoiceNumber: string;
    qbInvoiceNo: string;
    reckonInvoiceNo: string;
    statusId: number;
    customers: GenericList[];
    statusList: GenericList[];
    searched: boolean;
}

interface GenericList {
    id: number;
    name: string;
}

interface InvoiceDetails {
    id: number;
    companyId: number;          //job company id
    invoiceCompanyId: number;   //invoice company id => used to send to the correct Reckon
    customer: string;
    customerId: number;
    customerType: string;
    jobId: number;
    siteAddress: string;
    type: string;
    sendTo: string;
    batchNumber: string;
    reckonInvoiceId: string;
    canUpdateReckonInvoiceId: boolean;
    quoteNumber: string;
    hirePeriod: string;
    invoiceDate: string;
    dueDate: string;
    paidDate: string;
    paidDateActual: string;
    amount: string;
    errorMessage: string;
    status: string;
    statusId: number;
    canEdit: boolean;
    canDelete: boolean;
    invoiceStartDate: string;
    invoiceStartDateFormatted: string;
    isDirty: boolean;
    items: InvoiceItems[];
    showDetail: boolean;
    errors: any;
}

interface InvoiceItems {
    itemIndex: number;
    id: number;
    jobInvoiceId: number;
    branchIdent: string;
    item: string;
    itemId: number;
    itemTypeId: number;
    invItemType: string;
    invItemTypeId: number;
    quantityType: string;
    purchaseOrderNumber: string;
    additionalHireRate: string;
    rate: string;
    rateFormatted: string;
    gst: string;
    gstFormatted: string;
    chargeGst: boolean;
    amount: string;
    amountFormatted: string;
    description: string;
    status: number;
    invoiceStartDate: string;
    invoiceEndDate: string;
    hirePeriodDays: string;
    hirePeriod: string;
    dayRate: string;
    weekMonthRate: string;
    invoiceStartDateFormatted: string;
    invoiceEndDateFormatted: string;
    isDirty: boolean;
}

export class InvoicingData extends React.Component<RouteComponentProps<{}>, Invoice> {
    constructor(props: any) {
        super(props);

        this.state = {
            loading: true,
            gridLoading: false,
            invoiceDetails: [],
            invoiceTotalFormatted: "$0.00",
            canEdit: false,
            canEditHirePeriod: false,
            canView: true,
            canSend: false,
            canUpdatePaid: false,
            page: 1,
            totalRecords: 0,
            pageSize: 30,
            customerId: -1,
            siteAddress: "",
            jobId: "",
            invoiceNumber: "",
            qbInvoiceNo: "",
            reckonInvoiceNo: "",
            statusId: -1,
            customers: [],
            statusList: [],
            searched: false
        };
    }

    componentDidMount() {
        this.getData();
    }

    async getData() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/User/CheckInvoicingPermission?SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.canView == true) {
                this.setState({ canEdit: res.data.canEdit, canEditHirePeriod: res.data.canEditHirePeriod, canSend: res.data.canSend, canUpdatePaid: res.data.canUpdatePaid });

                //get dropdowns
                axios.get('api/Invoicing/GetInvoiceSearchDropdowns?SubId=' + user.sub, {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                })
                .then(result => {
                    this.setState({ customers: result.data.customers, statusList: result.data.statusOptions, loading: false });
                })
                .catch(error => {
                    toast.error(error.message);
                });
            } else {
                this.setState({ canView: res.data.canView });
                //redirect to 403 permission denied
                this.props.history.push("/accessdenied")
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    async getInvoices(page: number, getTotalRecords: boolean) {
        var totalRecords = getTotalRecords ? 0 : this.state.totalRecords;
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        //search button pressed or next page with search
        axios.get('api/Invoicing/SearchInvoices?subId=' + user.sub + '&page=' + page + '&totalRecords=' + totalRecords + '&getTotalRecords=' + getTotalRecords + "&customerId=" + this.state.customerId + "&siteAddress=" + this.state.siteAddress + "&jobId=" + this.state.jobId + "&invoiceNumber=" + this.state.invoiceNumber + "&qbInvoiceNo=" + this.state.qbInvoiceNo + "&reckonInvoiceNo=" + this.state.reckonInvoiceNo + "&statusId=" + this.state.statusId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var invoiceDetails = res.data.invoices;
            for (var i = 0; i < invoiceDetails.length; i++) {
                var invoice = invoiceDetails[i];

                for (var y = 0; y < invoice.items.length; y++) {
                    invoice.items[y].itemIndex = y;
                    invoice.items[y].amount = invoice.items[y].amount.toFixed(2);
                }
                //add blank row if canEdit invoice and user can edit
                if (invoice.canEdit && this.state.canEdit) {
                    var newRow: InvoiceItems = {
                        itemIndex: invoice.items.length,
                        id: 0,
                        jobInvoiceId: invoice.id,
                        branchIdent: "",
                        item: "",
                        itemId: -1,
                        itemTypeId: -1,
                        invItemType: "",
                        invItemTypeId: -1,
                        quantityType: "",
                        purchaseOrderNumber: "",
                        additionalHireRate: "0.00",
                        rate: "0.00",
                        rateFormatted: "$0.00",
                        gst: "0.00",
                        gstFormatted: "$0.00",
                        chargeGst: true,
                        amount: "",
                        amountFormatted: "$0.00",
                        description: "",
                        status: 1,
                        invoiceStartDate: "",
                        invoiceEndDate: "",
                        invoiceStartDateFormatted: "",
                        invoiceEndDateFormatted: "",
                        hirePeriodDays: "",
                        hirePeriod: "",
                        dayRate: "",
                        weekMonthRate: "",
                        isDirty: false
                    }
                    invoice.items.push(newRow);
                }
            }

            this.setState({ searched: true, gridLoading: false, invoiceDetails: invoiceDetails, invoiceTotalFormatted: res.data.invoiceTotalFormatted, page: page, totalRecords: res.data.totalRecords, pageSize: res.data.pageSize, loading: false });
        })
        .catch(error => {
            toast.error(error.message);
        });
 
    }

    reset = () => {
        this.setState({searched: false, gridLoading: false, customerId: -1, siteAddress: "", jobId: "", invoiceNumber: "", qbInvoiceNo: "", reckonInvoiceNo: "", statusId: -1, invoiceDetails: [] });
    }

    search = (e: any) => {
        e.preventDefault();
        if (this.state.customerId < 0 && !this.state.siteAddress && !this.state.jobId && !this.state.invoiceNumber && !this.state.qbInvoiceNo && !this.state.reckonInvoiceNo && this.state.statusId < 0) {
            toast.error("Please enter at least one search criteria!");
        } else {
            this.getInvoices(1, true);
            this.setState({ page: 1, searched: false, gridLoading: true });
        }
    }

    handleChange = (e: any) => {
        let change: any = {};
        change[e.target.name] = e.target.value;
        this.setState(change);
    }

    submit = (data: any) => {
        var invoices: InvoiceDetails[] = data.invoiceDetails;

        if (invoices.length > 0) {
            this.saveInvoices(invoices);
        } else {
            toast.info("No invoices have been changed");
        }
    }

    async saveInvoices(invoices: InvoiceDetails[]) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();
        var invoiceitems = [];
        for (var inv = 0; inv < invoices.length; inv++) {
            var item = {
                jobId: invoices[inv].jobId,
                customerId: invoices[inv].customerId,
                invoiceItems: invoices[inv].items.filter(i => i.isDirty)
            }
            invoiceitems.push(item);
        }

        var invDetails = {
            invoiceDetails: invoiceitems,
            subId: user.sub
        }

        //save
        axios.post('api/Invoicing/SaveInvoiceItems', invDetails, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.getInvoices(this.state.page, false);
            toast.success("Invoices Saved");
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    saveHirePeriod = (data: any) => {
        var invoices: InvoiceDetails[] = data.invoiceDetails;

        if (invoices.length > 0) {
            this.saveInvoiceHirePeriod(invoices);
        } else {
            toast.info("No invoices have been changed");
        }
    }

    async saveInvoiceHirePeriod(invoices: InvoiceDetails[]) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var invoiceitems = [];
        for (var inv = 0; inv < invoices.length; inv++) {
            var item = {
                jobId: invoices[inv].jobId,
                invoiceItems: invoices[inv].items.filter(i => i.isDirty)
            }
            invoiceitems.push(item);
        }

        var invDetails = {
            invoiceDetails: invoiceitems,
            subId: user.sub
        }

        //save
        axios.post('api/Invoicing/SaveInvoiceItemHirePeriod', invDetails, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.getInvoices(this.state.page, false);
            toast.success("Invoices Saved");
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    updateInvoiceDetails = (updatedInvoices: InvoiceDetails[]) => {
        this.setState({ invoiceDetails: updatedInvoices });
    }

    pageChanged = (page: number) => {
        this.setState({ page: page, gridLoading: true });
        this.getInvoices(page, false);
    }

    addNew = () => {
        this.props.history.push("/invoice")
    }

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();


        return (
            <div>
                <div className="makeitflexspacebetween">
                    <h1>Invoice Search</h1>
                    <button className="defaultbutton defaultbutton__small marginTop10" type='button' onClick={this.addNew}>Add New</button>
                </div>
                {contents}
            </div>
        )
    }

    renderDetails = () => {
        var contents = <Invoices invoiceDetails={this.state.invoiceDetails} invoiceTotalFormatted={this.state.invoiceTotalFormatted} showCustType={false} showJobInfo={true} showQuoteNo={false} showHirePeriod={true} showStatus={true} showStartDate={false} showPaidDate={true} showPaid={true} showSend={true} showRemove={false} showSentTo={false} showReckonInvNo={true} showError={false} showSent={false} canEdit={this.state.canEdit} canEditHirePeriod={this.state.canEditHirePeriod} canSend={this.state.canSend} canUpdatePaid={this.state.canUpdatePaid} page={this.state.page} pageSize={this.state.pageSize} totalRecords={this.state.totalRecords} pageChanged={this.pageChanged} beforeValid={this.updateInvoiceDetails} afterValid={this.submit} saveHirePeriod={this.saveHirePeriod} />

        return (
            <div>
                <div className="marginTop20">
                    <div className={this.state.canView ? "hidden" : ""}>
                        <h2 className="error">You do not have permission to view this page!</h2>
                    </div>
                    <div className={this.state.canView ? "" : "hidden"}>
                        <div className="input-group-parent">
                            <label className="input-group" htmlFor="customer">
                                <span className="label">Customer</span>
                                <select className="select" id="customer" name="customerId" value={this.state.customerId} onChange={(e) => this.handleChange(e)}>
                                    <option defaultValue="-1"></option>
                                    {this.state.customers.map(cust =>
                                        <option key={cust.id} value={cust.id}>{cust.name}</option>
                                    )};
                                </select>
                            </label>
                            <label className="input-group" htmlFor="siteAddress">
                                <span className="label label--setsizesm">Site Address</span>
                                <input className="input" type="text" id="siteAddress" name="siteAddress" value={this.state.siteAddress} onChange={(e) => this.handleChange(e)}></input>
                            </label>
                            <label className="input-group" htmlFor="jobId">
                                <span className="label label--setsizesm">Job Id</span>
                                <input className="input" type="text" id="jobId" name="jobId" value={this.state.jobId} onChange={(e) => this.handleChange(e)}></input>
                            </label>
                            <label className="input-group" htmlFor="status">
                                <span className="label">Status</span>
                                <select className="select" id="status" name="statusId" value={this.state.statusId} onChange={(e) => this.handleChange(e)}>
                                    <option defaultValue="-1"></option>
                                    {this.state.statusList.map(status =>
                                        <option key={status.id} value={status.id}>{status.name}</option>
                                    )};
                                </select>
                            </label>
                        </div>

                        <div className="input-group-parent">
                            <label className="input-group" htmlFor="invoiceNumber">
                                <span className="label">Dash Invoice No</span>
                                <input type='text' id="invoiceNumber" name="invoiceNumber" className='input' value={this.state.invoiceNumber} onChange={(e) => this.handleChange(e)}></input>
                            </label>
                            <label className="input-group" htmlFor="reckonInvoiceNo">
                                <span className="label">Reckon Invoice No</span>
                                <input type='text' id="reckonInvoiceNo" name="reckonInvoiceNo" className='input' value={this.state.reckonInvoiceNo} onChange={(e) => this.handleChange(e)}></input>
                            </label>
                        </div>

                        <div className="defaultbutton__container--right">
                            <button className="defaultbutton marginRight10" type='button' onClick={this.reset}>Reset</button>
                            <button className="defaultbutton" type='button' onClick={this.search}>Search</button>
                        </div>
                        <div>
                            <hr></hr>
                        </div>
                        <div className={this.state.gridLoading ? "" : "hidden"}>
                            <p><em>Loading...</em></p>
                        </div>
                        <div className={this.state.invoiceDetails.length > 0 && !this.state.gridLoading ? "" : "hidden"}>
                            {contents}
                        </div>
                        <div className={this.state.invoiceDetails.length == 0 && this.state.searched && !this.state.gridLoading ? "" : "hidden"}>
                            No Records Found - Please try modifying your search criteria
                        </div>
                    </div>
                </div>
            </div>
        );

    }

};

